import {Injectable} from '@angular/core';
import {MsalBroadcastService, MsalService} from '@azure/msal-angular';
import {EMPTY, Observable, of, ReplaySubject} from 'rxjs';
import {catchError, filter, map, switchMap} from 'rxjs/operators';
import {LoggingService} from '../../../../app/shared/services/logging.service';
import {AccountInfo, SilentRequest, RedirectRequest} from '@azure/msal-browser';

@Injectable({
  providedIn: 'root'
})
export class TorchTokenService {

  public msalAccountAvailable = new ReplaySubject<boolean>();
  public currentTokens = new ReplaySubject<{ torchToken: string; graphToken: string }>();

  private domainHint = 'myfedex.onmicrosoft.com';
  private loginHint: string;
  private sid: string;
  private tokenExpiresOn: Date;
  private torchToken: string;
  private graphToken: string;
  private scopes = [
    'openid',
    'profile',
    'User.Read',
  ];

  constructor(private auth: MsalService,
              private broadcastService: MsalBroadcastService,
              private loggingService: LoggingService) {
    this.msalAccountAvailable.pipe(filter(Boolean)).subscribe(() => this.loggingService.setupUser());
  }

  /**
   * Returns the current user's Graph grant token
   */
  getGraphToken(): Observable<string> {
    return this.currentTokens.pipe(map(tokens => tokens.graphToken));
  }

  /**
   * Returns the current TORCH id token
   *
   * @keep
   */
  getTorchToken(): Observable<string> {
    return this.currentTokens.pipe(map(tokens => tokens.torchToken));
  }

  /// MARK: Private

  /**
   * Get TORCH/Graph tokens
   *
   */
  public getTokens(): Observable<{ torchToken?: string; graphToken?: string }> {
    const account = this.getAccount();

    let request: SilentRequest = {
      scopes: this.scopes
    };

    if (!!account && !!account.username) {
      request = {...request, account};
    } else {
      this.loggingService.log('No account for getTokens()');
    }

    if (!!this.tokenExpiresOn && this.tokenExpiresOn <= new Date()) {
      this.loggingService.log('Forcing refresh');
      request = {...request, forceRefresh: true};
    } else if (!!this.torchToken && this.graphToken.length > 0 && !!this.graphToken && this.torchToken.length > 0) {
      return of({torchToken: this.torchToken, graphToken: this.graphToken});
    }

    return this.auth.acquireTokenSilent(request).pipe(
      catchError(() => this.acquireTokenInteractively(request, this.scopes)),
      map((response) => {
        if (!!response) {
          const idToken = response.idToken;
          const accessToken = response.accessToken;

          this.processAccountClaims(response.account.idTokenClaims);
          this.tokenExpiresOn = response.expiresOn;
          this.torchToken = idToken;
          this.graphToken = accessToken;
          this.checkIfAccountAvailable();

          const tokens = {torchToken: this.torchToken, graphToken: this.graphToken};

          this.currentTokens.next(tokens);

          return tokens;
        }
      }),
    );
  }

  /**
   * Call this function when you're having errors like 'Application requested a user session which does not exist'
   */
  public redirectLogin() {
    const account = this.getAccount();

    let request: RedirectRequest = {
      scopes: this.scopes
    };

    if (!!account && !!account.username) {
      request = {...request, account};
    } else {
      this.loggingService.log('No account for redirectLogin()');
    }

    return this.acquireTokenInteractively(request, this.scopes);
  }

  private processAccountClaims(claims: Record<string, any>) {
    if (claims.hasOwnProperty('sid')) {
      this.sid = claims.sid;
      this.loggingService.log('Account Claims - Session ID', this.sid);
    }

    if (claims.hasOwnProperty('preferred_username')) {
      this.loginHint = claims.preferred_username;
      this.loggingService.log('Account Claims - Preferred Username', this.loginHint);
    }
  }

  /**
   * Check if MSAL has any accounts
   *
   * @private
   */
  private hasAccounts(): boolean {
    return this.auth.instance.getAllAccounts().length > 0;
  }

  /**
   * Check if MSAL has an active account
   *
   * @private
   */
  private checkIfAccountAvailable(): void {
    let account = this.auth.instance.getActiveAccount();

    if (!account && this.auth.instance.getAllAccounts().length > 0) {
      account = this.auth.instance.getAllAccounts()[0];
    }

    if (!!account && !!account.username) {
      this.msalAccountAvailable.next(true);
    } else {
      this.msalAccountAvailable.next(false);
    }
  }

  /**
   * Get MSAL's current active account
   *
   * @private
   */
  private getAccount(): AccountInfo | null {
    const availableAccounts = this.auth.instance.getAllAccounts();
    let account = this.auth.instance.getActiveAccount();

    if (!account && this.hasAccounts()) {
      account = availableAccounts[0];
      this.auth.instance.setActiveAccount(account);
    }

    if (!this.hasAccounts()) {
      this.loggingService.log('No accounts available');
      return null;
    }

    return account;
  }

  /**
   * Invoke interaction for the given set of scopes
   *
   * @param authRequest Request
   * @param scopes Array of scopes for the request
   * @param forceRedirect Force a redirect
   * @returns Result from the interactive request
   */
  private acquireTokenInteractively(authRequest: SilentRequest, scopes: string[], forceRedirect: boolean = false) {
    const account = this.getAccount();
    if (!!account && !forceRedirect) {
      return this.auth.acquireTokenSilent({...authRequest, account});
    }

    return this.auth.acquireTokenRedirect({
      ...authRequest, scopes, sid: this.sid, loginHint: this.loginHint, domainHint: this.domainHint
    }).pipe(
      catchError(() => EMPTY),
      switchMap(() => EMPTY)
    );
  }
}
